import React, { useEffect, useState } from 'react';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { useNavigate } from 'react-router-dom';
import Modal from '../UI/Modal';
import userService from '../../api/services/user.service';
import {
  TrophyIcon,
  PuzzlePieceIcon,
  CreditCardIcon,
  BanknotesIcon,
  UserGroupIcon,
  CurrencyDollarIcon,
  PencilSquareIcon,
  CheckIcon,
  XMarkIcon,
} from '@heroicons/react/24/outline';

interface UserDetailModalProps {
  isOpen: boolean;
  userId: string | null;
  onClose: () => void;
}

const StatBox: React.FC<{
  label: string;
  value: string | number;
  icon: React.ReactNode;
  color?: string;
  onClick?: () => void;
  title?: string;
}> = ({ label, value, icon, color = 'text-primary-500', onClick, title }) => {
  const inner = (
    <>
      <div className="flex items-center gap-2 mb-1">
        <span className={color}>{icon}</span>
        <p className="text-[10px] text-gray-500 uppercase tracking-wide">
          {label}
        </p>
      </div>
      <p className="text-base font-bold text-gray-900">{value}</p>
    </>
  );

  if (onClick) {
    return (
      <button
        type="button"
        onClick={onClick}
        title={title}
        className="text-left border border-gray-100 rounded-lg p-3 hover:border-primary-200 hover:bg-primary-50/40 transition-colors cursor-pointer"
      >
        {inner}
      </button>
    );
  }

  return (
    <div className="border border-gray-100 rounded-lg p-3">{inner}</div>
  );
};

const UserDetailModal: React.FC<UserDetailModalProps> = ({
  isOpen,
  userId,
  onClose,
}) => {
  const queryClient = useQueryClient();
  const navigate = useNavigate();

  const { data, isLoading, error } = useQuery({
    queryKey: ['user-detail', userId],
    queryFn: () => userService.getOne(userId as string),
    enabled: isOpen && !!userId,
    staleTime: 60 * 1000,
  });

  const [editingBalance, setEditingBalance] = useState(false);
  const [balanceInput, setBalanceInput] = useState('');
  const [savingBalance, setSavingBalance] = useState(false);
  const [balanceError, setBalanceError] = useState<string | null>(null);

  useEffect(() => {
    setEditingBalance(false);
    setBalanceInput('');
    setBalanceError(null);
    setSavingBalance(false);
  }, [isOpen, userId]);

  const startEditBalance = () => {
    if (!data) return;
    setBalanceInput(String(data.user.balance));
    setBalanceError(null);
    setEditingBalance(true);
  };

  const cancelEditBalance = () => {
    setEditingBalance(false);
    setBalanceError(null);
    setBalanceInput('');
  };

  const saveBalance = async () => {
    if (!userId) return;

    const parsed = Number(balanceInput);
    if (!Number.isFinite(parsed) || parsed < 0) {
      setBalanceError('Enter a valid non-negative number');
      return;
    }

    setSavingBalance(true);
    setBalanceError(null);
    try {
      await userService.updateBalance(userId, parsed);
      await queryClient.invalidateQueries({
        queryKey: ['user-detail', userId],
      });
      await queryClient.invalidateQueries({ queryKey: ['users'] });
      setEditingBalance(false);
      setBalanceInput('');
    } catch (err) {
      setBalanceError(
        err instanceof Error ? err.message : 'Failed to update balance'
      );
    } finally {
      setSavingBalance(false);
    }
  };

  const handleBalanceKeyDown = (
    e: React.KeyboardEvent<HTMLInputElement>
  ) => {
    if (e.key === 'Enter') saveBalance();
    if (e.key === 'Escape') cancelEditBalance();
  };

  const goToGames = (mode: 'played' | 'won') => {
    if (!userId) return;
    onClose();
    navigate(`/users/${userId}/games?mode=${mode}`);
  };

  return (
    <Modal isOpen={isOpen} onClose={onClose} title="User Details" size="lg">
      {isLoading && (
        <div className="flex justify-center py-10">
          <div className="animate-spin rounded-full h-8 w-8 border-4 border-solid border-primary-500 border-t-transparent" />
        </div>
      )}

      {error && (
        <p className="text-sm text-red-500 py-4">
          {error instanceof Error ? error.message : 'Failed to load user'}
        </p>
      )}

      {data && (
        <div className="space-y-5">
          {/* Identity */}
          <div className="flex items-start gap-4">
            <div className="w-14 h-14 rounded-full bg-primary-100 flex items-center justify-center text-primary-700 text-lg font-semibold flex-shrink-0">
              {data.user.fullName
                .split(' ')
                .map((n) => n[0])
                .join('')
                .slice(0, 2)
                .toUpperCase() || '?'}
            </div>
            <div className="flex-1">
              <h3 className="text-lg font-semibold text-gray-900">
                {data.user.fullName}
              </h3>
              <p className="text-xs text-gray-500">
                @{data.user.username || 'no-username'} ·{' '}
                {data.user.telegramId}
              </p>
              <div className="flex items-center gap-2 mt-2">
                <span
                  className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[10px] font-medium ${
                    data.user.isActive
                      ? 'bg-green-50 text-green-700'
                      : 'bg-gray-100 text-gray-600'
                  }`}
                >
                  <span
                    className={`w-1.5 h-1.5 rounded-full ${
                      data.user.isActive ? 'bg-green-500' : 'bg-gray-400'
                    }`}
                  />
                  {data.user.isActive ? 'Active' : 'Disabled'}
                </span>
                {data.user.isPremium && (
                  <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[10px] font-medium bg-yellow-50 text-yellow-700">
                    ⭐ Premium
                  </span>
                )}
              </div>
            </div>

            {/* Balance + Edit */}
            <div className="text-right">
              <p className="text-[10px] text-gray-400">Balance</p>

              {!editingBalance ? (
                <div className="flex items-center justify-end gap-2">
                  <p className="text-lg font-bold text-gray-900">
                    ETB {data.user.balance.toLocaleString()}
                  </p>
                  <button
                    onClick={startEditBalance}
                    className="p-1 rounded-md hover:bg-primary-50 text-primary-600 transition-colors"
                    title="Edit balance"
                  >
                    <PencilSquareIcon className="w-4 h-4" />
                  </button>
                </div>
              ) : (
                <div className="flex flex-col items-end gap-1.5">
                  <div className="flex items-center gap-1.5">
                    <input
                      type="number"
                      min={0}
                      step="0.01"
                      value={balanceInput}
                      onChange={(e) => setBalanceInput(e.target.value)}
                      onKeyDown={handleBalanceKeyDown}
                      autoFocus
                      className="w-32 text-sm text-right border border-gray-200 rounded-md px-2 py-1 focus:outline-none focus:ring-2 focus:ring-primary-400"
                    />
                    <button
                      onClick={saveBalance}
                      disabled={savingBalance}
                      className="p-1.5 rounded-md bg-green-50 text-green-600 hover:bg-green-100 disabled:opacity-50"
                      title="Save"
                    >
                      <CheckIcon className="w-4 h-4" />
                    </button>
                    <button
                      onClick={cancelEditBalance}
                      disabled={savingBalance}
                      className="p-1.5 rounded-md bg-gray-50 text-gray-600 hover:bg-gray-100 disabled:opacity-50"
                      title="Cancel"
                    >
                      <XMarkIcon className="w-4 h-4" />
                    </button>
                  </div>
                  {balanceError && (
                    <p className="text-[10px] text-red-500">{balanceError}</p>
                  )}
                </div>
              )}
            </div>
          </div>

          {/* Basic info */}
          <div className="bg-gray-50 rounded-lg p-3 grid grid-cols-2 gap-y-2 gap-x-4 text-xs">
            <div>
              <p className="text-gray-500">Phone</p>
              <p className="font-medium text-gray-900">
                {data.user.phoneNumber || '—'}
              </p>
            </div>
            <div>
              <p className="text-gray-500">Language</p>
              <p className="font-medium text-gray-900 uppercase">
                {data.user.languageCode}
              </p>
            </div>
            <div>
              <p className="text-gray-500">Referred By</p>
              <p className="font-medium text-gray-900">
                {data.user.referredBy || '—'}
              </p>
            </div>
            <div>
              <p className="text-gray-500">Joined</p>
              <p className="font-medium text-gray-900">
                {new Date(data.user.createdAt).toLocaleDateString()}
              </p>
            </div>
          </div>

          {/* Stats grid */}
          <div>
            <h4 className="text-xs font-semibold text-gray-500 uppercase tracking-wide mb-2">
              Activity
            </h4>
            <div className="grid grid-cols-2 md:grid-cols-3 gap-2">
              <StatBox
                label="Games Played"
                value={data.stats.gamesPlayed.toLocaleString()}
                icon={<PuzzlePieceIcon className="w-4 h-4" />}
                onClick={() => goToGames('played')}
                title="View all games this user played"
              />
              <StatBox
                label="Total Wins"
                value={data.stats.totalWins.toLocaleString()}
                icon={<TrophyIcon className="w-4 h-4" />}
                color="text-green-500"
                onClick={() => goToGames('won')}
                title="View all games this user won"
              />
              <StatBox
                label="Total Prize"
                value={`ETB ${data.stats.totalPrize.toLocaleString()}`}
                icon={<CurrencyDollarIcon className="w-4 h-4" />}
                color="text-green-500"
              />
              <StatBox
                label="Credits"
                value={data.stats.totalCreditTransactions.toLocaleString()}
                icon={<CreditCardIcon className="w-4 h-4" />}
                color="text-primary-500"
              />
              <StatBox
                label="Total Credited"
                value={`ETB ${data.stats.totalCredited.toLocaleString()}`}
                icon={<CreditCardIcon className="w-4 h-4" />}
                color="text-primary-500"
              />
              <StatBox
                label="Withdrawals"
                value={data.stats.totalWithdrawals.toLocaleString()}
                icon={<BanknotesIcon className="w-4 h-4" />}
                color="text-red-500"
              />
              <StatBox
                label="Total Withdrawn"
                value={`ETB ${data.stats.totalWithdrawn.toLocaleString()}`}
                icon={<BanknotesIcon className="w-4 h-4" />}
                color="text-red-500"
              />
              <StatBox
                label="Referrals Made"
                value={data.stats.referredCount.toLocaleString()}
                icon={<UserGroupIcon className="w-4 h-4" />}
                color="text-secondary-500"
              />
              <StatBox
                label="Referral Revenue"
                value={`ETB ${data.stats.totalReferralRevenue.toLocaleString()}`}
                icon={<CurrencyDollarIcon className="w-4 h-4" />}
                color="text-secondary-500"
              />
            </div>
          </div>
        </div>
      )}
    </Modal>
  );
};

export default UserDetailModal;